import React, { useEffect, useState, useRef, createElement } from 'react';
import { PlayIcon, PauseIcon, Volume2Icon, AlertCircleIcon } from 'lucide-react';
export const Header = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  // Load Wistia scripts with preconnect optimization
  useEffect(() => {
    // Add preconnect for faster loading
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fast.wistia.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://embed-ssl.wistia.com';
    document.head.appendChild(preconnect2);
    // Load player script immediately (not async for faster initial load)
    const playerScript = document.createElement('script');
    playerScript.src = 'https://fast.wistia.com/player.js';
    document.body.appendChild(playerScript);
    // Load embed script
    const embedScript = document.createElement('script');
    embedScript.src = 'https://fast.wistia.com/embed/obns1fmrny.js';
    embedScript.type = 'module';
    document.body.appendChild(embedScript);
    return () => {
      document.head.removeChild(preconnect1);
      document.head.removeChild(preconnect2);
      document.body.removeChild(playerScript);
      document.body.removeChild(embedScript);
    };
  }, []);
  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing');
    pricingSection?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const togglePlay = async () => {
    if (!audioRef.current) return;
    try {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        // Reset error state before attempting to play
        setError(null);
        const playPromise = audioRef.current.play();
        if (playPromise !== undefined) {
          await playPromise;
          setIsPlaying(true);
        }
      }
    } catch (error) {
      console.error('Error playing audio:', error);
      setError('No se pudo reproducir el audio');
      setIsPlaying(false);
    }
  };
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => {
      if (audio.duration && !isNaN(audio.duration)) {
        setDuration(audio.duration);
        setIsLoading(false);
      }
    };
    const handleEnded = () => setIsPlaying(false);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleCanPlay = () => {
      setIsLoading(false);
      setError(null);
    };
    const handleError = (e: Event) => {
      console.error('Audio error:', e);
      setError('Error al cargar el audio');
      setIsLoading(false);
      setIsPlaying(false);
    };
    const handleLoadStart = () => setIsLoading(true);
    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('error', handleError);
    audio.addEventListener('loadstart', handleLoadStart);
    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('loadstart', handleLoadStart);
    };
  }, []);
  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTime = parseFloat(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };
  return <>
      <style dangerouslySetInnerHTML={{
      __html: `
          wistia-player[media-id='obns1fmrny']:not(:defined) {
            background: center / contain no-repeat url('https://fast.wistia.com/embed/medias/obns1fmrny/swatch');
            display: block;
            filter: blur(5px);
            padding-top: 56.25%;
          }
        `
    }} data-id="element-130" />

      <header className="bg-navy py-4 md:py-8 px-6 md:px-10" data-id="element-131">
        <div className="max-w-7xl mx-auto flex flex-col items-center gap-4 md:gap-6" data-id="element-132">
          {/* Headline */}
          <h1 className="text-3xl md:text-5xl font-bold text-cream text-center" data-id="element-133">
            ¿DÓNDE SE VA MI DINERO?
          </h1>

          {/* Video Player */}
          <div className="w-full max-w-3xl rounded-lg overflow-hidden border border-gold/20" data-id="element-134">
            {createElement('wistia-player', {
            'media-id': 'obns1fmrny',
            aspect: '1.7777777777777777',
            preload: 'metadata'
          })}
          </div>

          {/* Audio Player */}
          <div className="flex items-center gap-3 bg-navy/50 backdrop-blur-sm rounded-full px-4 py-2 border border-gold/20" data-id="element-135">
            <Volume2Icon className="w-4 h-4 text-gold" data-id="element-136" />
            <span className="text-cream text-sm font-medium hidden sm:inline" data-id="element-137">
              Preview del Ebook
            </span>
            <button onClick={togglePlay} disabled={isLoading || !!error} className="bg-gold hover:bg-gold/90 text-navy rounded-full p-2 transition-all duration-300 hover:scale-110 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100" aria-label={isPlaying ? 'Pausar audio' : 'Reproducir audio'} data-id="element-138">
              {isLoading ? <div className="w-4 h-4 border-2 border-navy border-t-transparent rounded-full animate-spin" data-id="element-139" /> : error ? <AlertCircleIcon className="w-4 h-4" data-id="element-140" /> : isPlaying ? <PauseIcon className="w-4 h-4" data-id="element-141" /> : <PlayIcon className="w-4 h-4" data-id="element-142" />}
            </button>
            <div className="flex items-center gap-2" data-id="element-143">
              <span className="text-cream text-xs font-mono" data-id="element-144">
                {formatTime(currentTime)}
              </span>
              <input type="range" min="0" max={duration || 0} value={currentTime} onChange={handleSeek} disabled={isLoading || !!error} className="w-24 h-1 bg-cream/20 rounded-full appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-gold [&::-webkit-slider-thumb]:cursor-pointer" data-id="element-145" />
              <span className="text-cream/60 text-xs font-mono" data-id="element-146">
                {formatTime(duration)}
              </span>
            </div>
            <audio ref={audioRef} src="https://share.aielitesolutions.com/files/692a3e397f7e8c1c36a4c0f6/fc061da1-9d2b-460d-bf4d-d1ee375bb851" preload="auto" crossOrigin="anonymous" data-id="element-147" />
          </div>
        </div>
      </header>
    </>;
};